import type { LucideIcon } from 'lucide-react';
import {
  Building2, CalendarDays, CreditCard, ChartNoAxesCombined, CircleCheckBig, Clapperboard, CodeXml, FolderKanban, House, Languages, ListTodo, ListTree, Palette, Plug, Radio, Send, Settings, Sparkles, Users,
} from 'lucide-react';
import type { StringKey } from '@castfeeds/i18n';
import { SOON } from '@/lib/constants';

export interface NavItem {
  id: string;
  label: StringKey;
  icon: LucideIcon;
  to: string;
  /** Set when the screen is not in Pilot 1: the phase it ships in. */
  soon?: 'P1' | 'P2';
}

const item = (id: string, label: StringKey, icon: LucideIcon, to: string): NavItem => ({ id, label, icon, to: SOON[id] ? `/soon/${id}` : to, soon: SOON[id] });

/** Sidebar groups, in the prototype's order (Workspace / Create / Deliver / Admin). */
export const NAV: { group: StringKey; items: NavItem[] }[] = [
  { group: 'grpWork', items: [item('home', 'nHome', House, '/'), item('insights', 'nInsights', ChartNoAxesCombined, ''), item('projects', 'nProjects', FolderKanban, '/projects'), item('customers', 'nCustomers', Building2, '/customers'), item('brand', 'nBrand', Palette, '/brand-kits')] },
  { group: 'grpCreate', items: [item('episode', 'nEpisode', ListTree, '/episodes'), item('live', 'nLive', Radio, '/live'), item('director', 'nDirector', Sparkles, ''), item('editor', 'nEditor', Clapperboard, '/editor'), item('localize', 'nLocalize', Languages, '')] },
  { group: 'grpDeliver', items: [item('tasks', 'nTasks', ListTodo, '/tasks'), item('approvals', 'nApprovals', CircleCheckBig, '/approvals'), item('publish', 'nPublish', Send, '/publish'), item('calendar', 'nCalendar', CalendarDays, '/calendar'), item('player', 'nPlayer', CodeXml, '')] },
  { group: 'grpAdmin', items: [item('team', 'nTeam', Users, '/team'), item('billing', 'nBilling', CreditCard, '/billing'), item('integrations', 'nInteg', Plug, '/integrations'), item('settings', 'nSettings', Settings, '/settings')] },
];

export const NAV_ITEMS = NAV.flatMap((g) => g.items);

/** Which nav item a path belongs to (for aria-current and the breadcrumb). */
export function navIdFor(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/soon/')) return pathname.split('/')[2] ?? 'home';
  if (/^\/projects\/[^/]+\/episodes\//.test(pathname) || pathname.startsWith('/episodes')) return 'episode';
  if (pathname.startsWith('/projects')) return 'projects';
  if (pathname.startsWith('/customers')) return 'customers';
  if (pathname.startsWith('/brand-kits')) return 'brand';
  if (pathname.startsWith('/editor')) return 'editor';
  if (pathname.startsWith('/approvals')) return 'approvals';
  if (pathname.startsWith('/tasks')) return 'tasks';
  if (pathname.startsWith('/publish')) return 'publish';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/team')) return 'team';
  if (pathname.startsWith('/billing')) return 'billing';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname === '/live' || pathname.startsWith('/live/')) return 'live';
  if (pathname.startsWith('/integrations')) return 'integrations';
  return '';
}
